import type { Run } from '@/types/monitoring';

export interface PollForLatestRunIdDeps {
  fetchRuns: (args: {
    projectName: string;
    appName: string;
    workflowName: string;
    accessToken: string;
  }) => Promise<Run[]>;
  getOttoToken: () => Promise<string | null>;
  delay?: (ms: number) => Promise<void>;
}

export interface PollForLatestRunIdArgs {
  projectName?: string | undefined;
  appName?: string | undefined;
  workflowName?: string | undefined;
  maxAttempts?: number;
  delayMs?: number;
}

const defaultDelay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * Poll the runs API briefly to find the run with the most recent startTime,
 * used to recover a runId when `/submitDraftRun` omits the
 * `x-ms-workflow-run-id` response header.
 *
 * Returns the newest run's id, or `undefined` if the polling window elapses
 * without finding one. Swallows per-attempt fetch errors — the run may not
 * have surfaced in the API yet — but returns early on auth failure.
 *
 * Pure with respect to injected dependencies (network + timer are ports).
 */
export async function pollForLatestRunId(
  args: PollForLatestRunIdArgs,
  deps: PollForLatestRunIdDeps
): Promise<string | undefined> {
  const { projectName, appName, workflowName, maxAttempts = 6, delayMs = 500 } = args;
  if (!projectName || !workflowName || !appName) return undefined;

  const { fetchRuns, getOttoToken, delay = defaultDelay } = deps;

  let token: string | null;
  try {
    token = await getOttoToken();
  } catch {
    return undefined;
  }
  if (!token) return undefined;

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    try {
      const runs = await fetchRuns({ projectName, appName, workflowName, accessToken: token });
      if (runs.length > 0) {
        const newest = runs.reduce((a, b) =>
          new Date(b.startTime).getTime() > new Date(a.startTime).getTime() ? b : a
        );
        if (newest?.id) return newest.id;
      }
    } catch {
      // Per-attempt failure — keep polling; the run may not have surfaced yet.
    }
    if (attempt < maxAttempts - 1) {
      await delay(delayMs);
    }
  }
  return undefined;
}
